import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { CgGenderFemale, CgGenderMale } from "react-icons/cg";
import {
  FaBookReader,
  FaChalkboardTeacher,
  FaClock,
  FaDiscourse,
  FaSchool,
  FaSearchLocation,
} from "react-icons/fa";
import educationData from "../../i18n/education_levels.json"; // Import the education levels JSON
import subjectsData from "../../i18n/general_tutoring_subjects.json"; // Import the subjects JSON
import locationsData from "../../i18n/hong_kong_locations.json";
import typeOptionValue from "../../i18n/type_option_value.json"; // Adjust the path accordingly
import "../List.css"; // Import CSS for styling

function AdCardStudent({ ad, isViewScreen = false, onAdInfoClick }) {
  const { t } = useTranslation(); // Initialize translation

  // Extract subcategories
  const subcategoryList = subjectsData.subjects.flatMap((subject) =>
    subject.subcategories.map((subcategory) => ({
      value: subcategory.value,
      display: subcategory.display[t("language")],
      subject: subject.display[t("language")],
    }))
  );

  const SubjectDisplay = ({ values }) => {
    let parsedValues = [];
    try {
      if (Array.isArray(values)) {
        parsedValues = values;
      } else if (typeof values === "string") {
        parsedValues = JSON.parse(values);
      }
    } catch (e) {
      // fallback: treat as single value or empty
      parsedValues = [];
    }
    return (
      <div className="flex flex-wrap">
        {Array.isArray(parsedValues)
          ? parsedValues.map((value) => {
              const subject = subcategoryList.find(
                (sub) => sub.value === value
              );
              if (subject) {
                return (
                  <span className="pr-2" key={subject.value}>
                    {subject.display}
                  </span>
                );
              }
              return null;
            })
          : null}
      </div>
    );
  };
  // Extract all areas from the locations JSON
  const locationsAreas = locationsData.regions.flatMap((region) =>
    region.areas.map((area) => ({
      value: area.value,
      display: area.display[t("language")], // Change to t('language') for localization
      region: region.display[t("language")], // Include region name for context
    }))
  );
  const LocationDisplay = ({ values }) => {
    return (
      <div className="flex flex-wrap">
        {JSON.parse(values).map((value) => {
          const locationEntry = locationsAreas.find(
            (location) => location.value === value
          );
          return locationEntry ? (
            <span className="pr-2" key={locationEntry.value}>
              {locationEntry.display}
            </span>
          ) : null;
        })}
      </div>
    );
  };
  // Extract education levels and their grades from the JSON data
  const educationLevels = educationData.flatMap((level) =>
    // Use current language from i18n
    ({
      value: level.value,
      display: level.display[t("language")],
    })
  );

  const EducationLevelDisplay = ({ values }) => {
    return (
      <div className="flex flex-wrap">
        {JSON.parse(values).map((value) => {
          const gradeEntry = educationLevels.find(
            (grade) => grade.value === value
          );
          return gradeEntry ? (
            <span className="pr-2" key={gradeEntry.value}>
              {gradeEntry.display}
            </span>
          ) : null;
        })}
      </div>
    );
  };
  const FeeTypeDisplay = ({ feeType }) => {
    const { t } = useTranslation();
    const feeTypesEntry = Object.entries(typeOptionValue.fee_types).find(
      ([key]) => key === feeType
    );

    return feeTypesEntry ? (
      <span className="fee" key={feeType}>
        {feeTypesEntry[1].display[t("language")]}
      </span>
    ) : null;
  };

  const SubjectTypeDisplay = ({ values }) => {
    const { t } = useTranslation();

    return (
      <div className="flex flex-wrap">
        {JSON.parse(values).map((value) => {
          const subjectEntry = Object.entries(
            typeOptionValue.subject_types
          ).find(([key]) => key === value);
          return subjectEntry ? (
            <span className="pr-1" key={value}>
              {subjectEntry[1].display[t("language")]}
            </span>
          ) : null;
        })}
      </div>
    );
  };

  const FormGroupDisplay = ({ values }) => {
    const { t } = useTranslation();

    return (
      <div className="flex flex-wrap">
        {JSON.parse(values).map((value) => {
          const groupType = Object.entries(
            typeOptionValue.form_group_types
          ).find(([key]) => key === value);
          return groupType ? (
            <span className="pr-1" key={value}>
              {groupType[1].display[t("language")]}
            </span>
          ) : null;
        })}
      </div>
    );
  };

  const [showFullContent, setShowFullContent] = useState(false);
  const descriptionRef = useRef(null);
  const [isOverLineClamp, setIsOverLineClamp] = useState(false);
  useLayoutEffect(() => {
    if (descriptionRef.current) {
      const descriptionElement = descriptionRef.current;
      const rect = descriptionElement.getBoundingClientRect();
      const isOverflow = rect.height < descriptionElement.scrollHeight;
      setIsOverLineClamp(isOverflow);
    }
  }, []);
  return (
    <div
      className={` p-2 ${
        isViewScreen === false ? "hover:border-green-300  border " : ""
      } cursor-pointer group relative `}
      key={ad.id}
    >
      <div className="grid  grid-cols-3  gap-4">
        <div className="col-span-3  ">
          <div className="col-span-3 text-green-500 text-xl font-semibold mt-1">
            <div className=" flex  justify-between   ">
              <SubjectDisplay values={ad.subject} />
              <div className="md:text-right mt-1 ">
                <div className="flex md:justify-end gap-1">
                  <div className="ad-fee  font-semibold text-2xl">
                    ${ad.fee}
                  </div>
                  <div className="pl-1 text-green-500 flex items-center text-md  font-semibold">
                    <FaClock className=" mr-2 size-3" />
                    <FeeTypeDisplay feeType={ad.feeType} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-3 mt-1 items-center text-gray-500 text-md font-semibold  grid  grid-cols-2 ">
            <div className=" flex *:mr-4 items-center ">
              <FaChalkboardTeacher size={17} />
              <FormGroupDisplay values={ad.formGroupType} />
            </div>
            <div className=" flex *:mr-4 items-center">
              <FaBookReader size={17} />
              <SubjectTypeDisplay values={ad.subjectType} />
            </div>

            <div className=" flex *:mr-4 items-center">
              <FaSchool size={17} />
              <EducationLevelDisplay values={ad.gradeLevel} />
            </div>
            <div className=" flex *:mr-4 items-center">
              <FaSearchLocation size={17} />
              <LocationDisplay values={ad.location} />
            </div>
          </div>
        </div>
        <div className="col-span-3 ">
          <div className="  flex justify-between">
            <div className="flex font-semibold text-xl items-center">
              {ad.username}
              {ad.gender === "female" ? (
                <CgGenderFemale className="size-7 text-pink-500" />
              ) : (
                <CgGenderMale className="size-7 text-blue-600" />
              )}
            </div>
            {/* Floating button in top-right corner on hover (desktop) */}
            {isViewScreen === false && (
              <div
                className="    "
                onClick={
                  onAdInfoClick
                    ? (e) => {
                        e.stopPropagation();
                        onAdInfoClick(ad);
                      }
                    : undefined
                }
              >
                <div className="flex items-center bg-white p-2 rounded shadow">
                  <FaDiscourse className="text-green-500 hover:text-green-700 size-5 cursor-pointer" />
                  <div className="text-green-500  hover:text-green-700 text-base">
                    {t("comment")}
                  </div>
                </div>
              </div>
            )}
          </div>
          {ad.requirements && (
            <div className="mt-2">
              <div
                ref={descriptionRef}
                className={`text-sm md:text-base  leading-relaxedoverflow-hidden 
            line-clamp-3  text-ellipsis  transition-all duration-300 
               ${showFullContent ? "line-clamp-none" : ""}`}
              >
                {ad.requirements}
              </div>
              {isOverLineClamp && !showFullContent && (
                <button
                  className="text-orange-400 hover:text-orange-500 mt-2"
                  onClick={() => setShowFullContent(true)}
                >
                  {t("more")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdCardStudent;
